import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Edit,
  Trash2,
  Dumbbell,
  Eye,
  EyeOff,
  Loader2,
  Sparkles,
} from 'lucide-react';
import TrialTemplateWorkoutAdapter from './TrialTemplateWorkoutAdapter';

const DIFFICULTY_LABELS = {
  beginner: 'Principiante',
  intermediate: 'Intermedio',
  advanced: 'Avanzato',
  expert: 'Expert',
};

const GOAL_LABELS = {
  strength: 'Forza',
  hypertrophy: 'Ipertrofia',
  endurance: 'Resistenza',
  flexibility: 'Flessibilità',
  general: 'Generale',
};

/**
 * Gestione template precompilati per palestre in trial (solo admin).
 */
const AdminTrialTemplatesPage = () => {
  const { getAuthenticatedApi } = useAuth();
  const { t } = useTranslation();
  const api = getAuthenticatedApi();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editorMode, setEditorMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin_trial_workout_templates.php?action=list');
      setTemplates(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleCreate = () => {
    setEditorMode('new');
    setSelectedId(null);
    setShowEditor(true);
  };

  const handleEdit = (template) => {
    setEditorMode('edit');
    setSelectedId(template.id);
    setShowEditor(true);
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setSelectedId(null);
  };

  const handleEditorSuccess = () => {
    setShowEditor(false);
    setSelectedId(null);
    fetchTemplates();
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Eliminare il template "${template.name}"?`)) return;
    try {
      await api.post(`/admin_trial_workout_templates.php?action=delete&id=${template.id}`);
      fetchTemplates();
    } catch (err) {
      console.error(err);
      alert('Errore durante l\'eliminazione');
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await api.post(`/admin_trial_workout_templates.php?action=toggle_active&id=${template.id}`);
      fetchTemplates();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="p-6 max-w-[1700px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-amber-500" />
            {t('admin.trialTemplates.title', 'Template trial')}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t(
              'admin.trialTemplates.subtitle',
              'Schede precompilate visibili solo alle palestre con trial attivo. I gestori possono importarle nella propria libreria template.'
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={handleCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
        >
          <Plus className="h-5 w-5" />
          {t('admin.trialTemplates.create', 'Nuovo template')}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600 dark:text-indigo-400" />
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-900">
          <Dumbbell className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400">
            {t('admin.trialTemplates.empty', 'Nessun template trial creato.')}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {templates.map((template) => (
            <div
              key={template.id}
              className={`rounded-xl border p-5 shadow-sm transition-colors ${
                template.is_active
                  ? 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700'
                  : 'bg-gray-50 dark:bg-gray-950 border-gray-200 dark:border-gray-800 opacity-75'
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{template.name}</h3>
                  {template.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                      {template.description}
                    </p>
                  )}
                </div>
                <span
                  className={`shrink-0 text-xs px-2 py-1 rounded-full ${
                    template.is_active
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                      : 'bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                  }`}
                >
                  {template.is_active ? 'Attivo' : 'Nascosto'}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400 mb-4">
                <span className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                  {DIFFICULTY_LABELS[template.difficulty_level] || template.difficulty_level}
                </span>
                <span className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                  {GOAL_LABELS[template.target_goal] || template.target_goal}
                </span>
                <span className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                  {template.exercise_count || 0} esercizi
                </span>
                {template.estimated_duration && (
                  <span className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                    ~{template.estimated_duration} min
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleEdit(template)}
                  className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50"
                >
                  <Edit className="h-4 w-4" />
                  Modifica
                </button>
                <button
                  type="button"
                  onClick={() => handleToggleActive(template)}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                  title={template.is_active ? 'Nascondi' : 'Attiva'}
                >
                  {template.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(template)}
                  className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                  title="Elimina"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showEditor && (
        <TrialTemplateWorkoutAdapter
          mode={editorMode}
          templateId={selectedId}
          onClose={handleEditorClose}
          onSuccess={handleEditorSuccess}
        />
      )}
    </div>
  );
};

export default AdminTrialTemplatesPage;
